import React, { useMemo } from 'react';
import classnames from 'classnames';
import { TableColumn } from 'tea-component';
import ReportTable from '../../../components/ReportTable';
import useDPSReportModule from '../../../_hooks/useDPSReportModule';
import { addMockIdBatch, getRecordKeyOfMockable } from '@gp/utils/common';
import { CARD_TITLE, buildRecord, removeDuplicatedChanges, getColumns } from './utils';
import { BoolFlag } from '@gp/enums/common';

import type { IBaseProps } from '@home/types/common';
import type { IDPSReportChangeDTO, IDPSReportChangeVO, IChangesProjectMilestoneDTOV2 } from '@gp/types/dpsReport/v2';

import './index.scss';

interface IChangesProps extends IBaseProps {
  // 是否loading
  isLoading?: boolean;
  projectsMilestones?: IChangesProjectMilestoneDTOV2[];
  // 模块数据
  value: IDPSReportChangeVO[];
  // 模块数据更新，删除后立马更新，新增后 需要等新增这条数据编辑完整后 才会更新，页面就不需要考虑不完整的脏数据了
  onChange?: (payload: IDPSReportChangeVO[]) => void;
}

const Changes: React.FC<IChangesProps> = (props) => {
  const { style, className, isLoading, projectsMilestones, value, onChange } = props;

  const { records, setRecords, onAdd, onDelete } = useDPSReportModule<IDPSReportChangeVO>({
    buildRecord,
    value,
    onChange,
  });

  const columns = useMemo<TableColumn<IDPSReportChangeVO>[]>(() => {
    const projects = projectsMilestones?.map((item) => {
      const { project_id: projectId, project } = item || {};
      return {
        text: project || '',
        value: projectId || '',
      };
    });
    return getColumns({
      projects: projects || [],
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onProjectChange: async (payload: string, cvm: IDPSReportChangeVO) => {
        try {
          const project = projectsMilestones?.find((item) => {
            const { project_id: projectId } = item || {};
            return projectId === payload;
          });
          if (!project) return;
          const projectMilestones = project?.details?.map((item) => ({ ...project, ...item }));

          // 当前选的project下没有milestones集合数据，不能支撑自动带出数据交互，直接添加一条空数据，让用户自己手动填写
          if (!projectMilestones?.length) {
            const { mock_id: mockId } = cvm || {};
            const recordIdx = records.findIndex((item) => item?.mock_id === mockId);
            const recordTmp = {
              ...records[recordIdx],
              ...project,
              milestone_id: '',
              milestone: '',
              date: '',
              is_gr: BoolFlag.False,
              is_launch: BoolFlag.False,
              field: '',
              new_value: '',
              original_value: '',
              approved: '',
              memo: '',
              next_step: '',
            };
            const recordsTmp = [...records.slice(0, recordIdx), recordTmp, ...records.slice(recordIdx + 1)];
            setRecords(recordsTmp);
            onChange?.(recordsTmp);
            return;
          }

          // 当前选的project下有milestones集合数据，可以支撑自动带出数据交互，直接自动带出project下的milestones集合数据
          /**
           * 针对即将来临的Changes集合，进行去重处理
           * 1、即将来临的Changes集合 内部去重，根据getChangeRepeatCheckKey方法生成的 重复判定标准去重
           * 2、结合当前的Changes集合 进行去重，过滤掉当前Changes集合中已有的，根据getChangeRepeatCheckKey方法生成的 重复判定标准去重
           */
          const removedDuplicateData = removeDuplicatedChanges(projectMilestones, records);
          const recordsComming = addMockIdBatch<IDPSReportChangeDTO>(removedDuplicateData);
          const recordsTmp = [...records.slice(0, records.length - 1), ...recordsComming];
          setRecords(recordsTmp);
          onChange?.(recordsTmp);
        } catch (exception) {}
      },
      onChange: (cvm: IDPSReportChangeVO, key: string, payload: string) => {
        const recordIdx = records.findIndex((item) => {
          const { mock_id: mockId } = item || {};
          return mockId === cvm?.mock_id;
        });
        const recordTmp = {
          ...records[recordIdx],
          [key]: payload || '',
        };
        const recordsTmp = [...records.slice(0, recordIdx), recordTmp, ...records.slice(recordIdx + 1)];
        setRecords(recordsTmp);
        onChange?.(recordsTmp);
      },
    });
  }, [projectsMilestones, records, onChange]);

  return (
    <ReportTable
      style={style}
      className={classnames('dbg-dps-report__changes-table-card', className)}
      title={CARD_TITLE}
      isLoading={isLoading}
      // addBtnTips=""
      columns={columns}
      records={records}
      recordKey={getRecordKeyOfMockable}
      onAdd={onAdd}
      onDelete={onDelete}
    />
  );
};

export default Changes;
